import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ReplyToItem } from '../../../contexts/ReplyToContext';
import type { MentionedUser } from './MentionTextarea';
import {
  PAYMENT_UPDATED_PARAM,
  takeCheckoutResume,
  type CheckoutResume,
} from '../../../utils/hostedCheckout';

/**
 * What the thread composer holds while the spending-approval modal is up: the
 * message the user typed, its @mentions and the bookable reply-to items. A
 * same-tab card checkout unloads the page, so this is stashed before leaving
 * and handed back here when the checkout returns.
 */
export interface ThreadComposeResumeState {
  message: string;
  mentions: MentionedUser[];
  replyTo: ReplyToItem[];
}

export const THREAD_COMPOSE_RESUME_HOST = 'thread-compose';

export function threadComposeCheckoutResume(
  threadId: string | null,
  state: ThreadComposeResumeState,
): CheckoutResume<ThreadComposeResumeState> {
  return { host: THREAD_COMPOSE_RESUME_HOST, key: threadId, state };
}

interface Handlers {
  /** Card saved: re-open the approval modal with the pending message. */
  onCardAdded: (state: ThreadComposeResumeState) => void;
  /** Checkout abandoned: put the message back in the composer. */
  onCancelled: (state: ThreadComposeResumeState) => void;
}

/**
 * `ready` must only become true once the host is the instance that will stay
 * mounted: ThreadViewContainer renders ThreadView, swaps it for a loading
 * placeholder while it fetches the thread, then mounts a fresh ThreadView —
 * taking the record in that first, short-lived instance would lose it.
 */
export function useThreadComposeCheckoutResume(
  threadId: string | null,
  ready: boolean,
  handlers: Handlers,
): void {
  const [, setSearchParams] = useSearchParams();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    if (!ready) return;
    const resume = takeCheckoutResume<ThreadComposeResumeState>(
      THREAD_COMPOSE_RESUME_HOST,
      threadId,
    );
    if (!resume) return;
    if (resume.cardAdded) {
      handlersRef.current.onCardAdded(resume.state);
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(PAYMENT_UPDATED_PARAM);
          return next;
        },
        { replace: true },
      );
    } else {
      handlersRef.current.onCancelled(resume.state);
    }
  }, [threadId, ready]);
}
